import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { exchangeSession } from "@/lib/authApi";
import { ADMIN } from "@/constants/testIds/admin";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * Handles the `#session_id=...` URL fragment that Emergent appends after Google auth.
 * Exchanges it for a persistent httpOnly cookie via POST /api/auth/session and then
 * navigates to /admin/applications, carrying the resolved user via location state so
 * ProtectedRoute can skip an unnecessary /auth/me round-trip.
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            navigate("/admin/login", { replace: true });
            return;
        }

        const sessionId = decodeURIComponent(match[1]);

        (async () => {
            try {
                const user = await exchangeSession(sessionId);
                // Clear the fragment from the URL bar
                window.history.replaceState(
                    null,
                    "",
                    window.location.pathname + window.location.search
                );
                navigate("/admin/applications", { replace: true, state: { user } });
            } catch (err) {
                console.error("[auth-callback] exchange failed", err);
                const status = err?.response?.status;
                if (status === 403) {
                    setError(
                        "This Google account is not authorised for the admin area."
                    );
                } else {
                    setError("We couldn’t complete sign-in. Please try again.");
                }
            }
        })();
    }, [navigate]);

    return (
        <div
            data-testid={ADMIN.authCallbackProcessing}
            className="min-h-screen grid place-items-center bg-white px-6"
        >
            <div className="max-w-md text-center">
                {!error ? (
                    <>
                        <div className="inline-flex items-center gap-3 text-[12.5px] tracking-[0.22em] uppercase text-[#0F172A]">
                            <span className="inline-block w-2 h-2 bg-[#B45309] animate-pulse" />
                            Verifying session…
                        </div>
                        <p className="ihx-lead mt-5">
                            One moment while we hand off the secure session.
                        </p>
                    </>
                ) : (
                    <>
                        <span className="ihx-eyebrow text-[#991B1B]">Access denied</span>
                        <h1 className="font-serif text-[28px] mt-4 leading-tight">
                            {error}
                        </h1>
                        <button
                            type="button"
                            onClick={() => navigate("/admin/login", { replace: true })}
                            className="ihx-btn-primary mt-8"
                        >
                            Return to sign in
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
